import { useState } from 'react';
import { TYPES_DE_VOYAGE } from '../lib/trips.js';
import { centimesVersEuros, eurosVersCentimes, versChampDate } from '../lib/format.js';

/**
 * Ligne de participant dans le formulaire. La clé `cle` sert uniquement à React
 * pour suivre les lignes ; elle n'est jamais envoyée au serveur. Sans elle,
 * supprimer une ligne du milieu ferait glisser le contenu des suivantes.
 */
function ligneParticipant(participant = {}) {
  return {
    cle: crypto.randomUUID(),
    name: participant.name ?? '',
    age: participant.age === null || participant.age === undefined ? '' : String(participant.age),
    // Saisi en texte libre, séparé par des virgules, converti à l'envoi.
    restrictions: (participant.restrictions ?? []).join(', '),
  };
}

/** Valeurs du formulaire, à partir d'un voyage existant ou vides. */
function etatInitial(voyage) {
  return {
    name: voyage?.name ?? '',
    origin: voyage?.origin ?? '',
    destinationLabel: voyage?.destination.label ?? '',
    destinationType: voyage?.destination.type ?? 'city',
    dateMode: voyage?.dateMode ?? 'duration_only',
    startDate: versChampDate(voyage?.startDate),
    endDate: versChampDate(voyage?.endDate),
    startTime: voyage?.startTime ?? '',
    endTime: voyage?.endTime ?? '',
    durationDays: voyage?.durationDays ? String(voyage.durationDays) : '',
    period: voyage?.period ?? '',
    tripTypes: voyage?.tripTypes ?? [],
    tripTypeNotes: voyage?.tripTypeNotes ?? '',
    // Saisi en euros, converti en centimes à l'envoi.
    budgetEuros: centimesVersEuros(voyage?.budget?.amount),
    participants: (voyage?.participants ?? []).map(ligneParticipant),
    groupDescription: voyage?.groupDescription ?? '',
  };
}

/**
 * Formulaire des paramètres d'un voyage, partagé par la création et la
 * modification.
 */
export function TripForm({ voyage, libelleAction, onSubmit, onCancel }) {
  const [champs, setChamps] = useState(() => etatInitial(voyage));
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const datesFixees = champs.dateMode === 'fixed_dates';

  const modifier = (champ) => (evenement) =>
    setChamps((precedent) => ({ ...precedent, [champ]: evenement.target.value }));

  function modifierParticipant(cle, champ, valeur) {
    setChamps((precedent) => ({
      ...precedent,
      participants: precedent.participants.map((p) =>
        p.cle === cle ? { ...p, [champ]: valeur } : p,
      ),
    }));
  }

  function ajouterParticipant() {
    setChamps((precedent) => ({
      ...precedent,
      participants: [...precedent.participants, ligneParticipant()],
    }));
  }

  function retirerParticipant(cle) {
    setChamps((precedent) => ({
      ...precedent,
      participants: precedent.participants.filter((p) => p.cle !== cle),
    }));
  }

  function basculerType(type) {
    setChamps((precedent) => ({
      ...precedent,
      tripTypes: precedent.tripTypes.includes(type)
        ? precedent.tripTypes.filter((t) => t !== type)
        : [...precedent.tripTypes, type],
    }));
  }

  async function soumettre(evenement) {
    evenement.preventDefault();
    setErreur(null);
    setEnvoi(true);

    try {
      await onSubmit({
        name: champs.name.trim() || undefined,
        origin: champs.origin.trim() || null,
        destination: { label: champs.destinationLabel, type: champs.destinationType },
        dateMode: champs.dateMode,
        startDate: datesFixees ? champs.startDate || null : null,
        endDate: datesFixees ? champs.endDate || null : null,
        startTime: datesFixees ? champs.startTime || null : null,
        endTime: datesFixees ? champs.endTime || null : null,
        durationDays: datesFixees ? null : Number(champs.durationDays) || null,
        period: champs.period.trim() || null,
        tripTypes: champs.tripTypes,
        tripTypeNotes: champs.tripTypeNotes.trim() || null,
        budget: { amount: eurosVersCentimes(champs.budgetEuros), currency: 'EUR' },
        participants: champs.participants.map((p) => ({
          name: p.name.trim() || null,
          age: p.age.trim() === '' ? null : Number(p.age),
          restrictions: p.restrictions
            .split(',')
            .map((r) => r.trim())
            .filter(Boolean),
        })),
        groupDescription: champs.groupDescription.trim() || null,
      });
    } catch (echec) {
      setErreur(echec.message);
      setEnvoi(false);
    }
  }

  return (
    <form className="formulaire" onSubmit={soumettre} noValidate>
      {/* Deux colonnes sur bureau, empilées sur mobile : le voyage d'un côté,
          les gens de l'autre. D'une seule traite, le formulaire faisait
          plusieurs écrans de haut sur un grand écran à moitié vide. */}
      <div className="formulaire__colonne">
        <h2 className="formulaire__section">Le voyage</h2>

      <div className="champ">
        <label htmlFor="origin">Ville de départ</label>
        <input
          id="origin"
          type="text"
          placeholder="facultatif"
          value={champs.origin}
          onChange={modifier('origin')}
        />
        <p className="aide">
          Sert à situer les trajets d’arrivée et de départ dans le programme.
        </p>
      </div>

      <div className="champ">
        <label htmlFor="destinationLabel">Destination</label>
        <input
          id="destinationLabel"
          type="text"
          placeholder="Lisbonne, Islande, Toscane…"
          value={champs.destinationLabel}
          onChange={modifier('destinationLabel')}
          required
        />
      </div>

      <div className="champ">
        <label htmlFor="destinationType">Nature de la destination</label>
        <select
          id="destinationType"
          value={champs.destinationType}
          onChange={modifier('destinationType')}
        >
          <option value="city">Une ville</option>
          <option value="region">Une région</option>
          <option value="country">Un pays</option>
          <option value="undecided">Pas encore décidé</option>
        </select>
      </div>

      <fieldset className="groupe">
        <legend>Dates</legend>
        <label className="option">
          <input
            type="radio"
            name="dateMode"
            value="duration_only"
            checked={!datesFixees}
            onChange={modifier('dateMode')}
          />
          Je connais seulement la durée
        </label>
        <label className="option">
          <input
            type="radio"
            name="dateMode"
            value="fixed_dates"
            checked={datesFixees}
            onChange={modifier('dateMode')}
          />
          Mes dates sont fixées
        </label>
      </fieldset>

      {datesFixees ? (
        <div className="duo">
          <div className="champ">
            <label htmlFor="startDate">Départ</label>
            <input
              id="startDate"
              type="date"
              value={champs.startDate}
              onChange={modifier('startDate')}
            />
          </div>
          <div className="champ">
            <label htmlFor="endDate">Retour</label>
            <input
              id="endDate"
              type="date"
              value={champs.endDate}
              onChange={modifier('endDate')}
            />
          </div>
          <div className="champ">
            <label htmlFor="startTime">Heure de départ</label>
            <input
              id="startTime"
              type="time"
              value={champs.startTime}
              onChange={modifier('startTime')}
            />
          </div>
          <div className="champ">
            <label htmlFor="endTime">Heure de retour</label>
            <input
              id="endTime"
              type="time"
              value={champs.endTime}
              onChange={modifier('endTime')}
            />
          </div>
          <p className="aide aide--duo">
            Facultatives, mais elles changent le programme : partir à 18h ne
            laisse qu’une soirée le premier jour, et rentrer le matin ampute le
            dernier. L’itinéraire en tient compte.
          </p>
        </div>
      ) : (
        <div className="duo">
          <div className="champ">
            <label htmlFor="durationDays">Nombre de jours</label>
            <input
              id="durationDays"
              type="number"
              min="1"
              max="365"
              inputMode="numeric"
              value={champs.durationDays}
              onChange={modifier('durationDays')}
            />
          </div>
          <div className="champ">
            <label htmlFor="period">Période envisagée</label>
            <input
              id="period"
              type="text"
              placeholder="printemps 2027"
              value={champs.period}
              onChange={modifier('period')}
            />
          </div>
        </div>
      )}

      <div className="champ">
        <label htmlFor="budgetEuros">Budget indicatif (€)</label>
        <input
          id="budgetEuros"
          type="text"
          inputMode="decimal"
          placeholder="1200"
          value={champs.budgetEuros}
          onChange={modifier('budgetEuros')}
        />
        <p className="aide">Pour l’ensemble du voyage. Laissez vide si vous ne savez pas.</p>
      </div>

      <fieldset className="groupe">
        <legend>Type de voyage</legend>
        <div className="etiquettes">
          {TYPES_DE_VOYAGE.map((type) => (
            <label
              key={type}
              className={`etiquette${champs.tripTypes.includes(type) ? ' etiquette--active' : ''}`}
            >
              <input
                type="checkbox"
                checked={champs.tripTypes.includes(type)}
                onChange={() => basculerType(type)}
              />
              {type}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="champ">
        <label htmlFor="tripTypeNotes">Autre envie</label>
        <input
          id="tripTypeNotes"
          type="text"
          placeholder="thermalisme, observation des oiseaux…"
          value={champs.tripTypeNotes}
          onChange={modifier('tripTypeNotes')}
        />
      </div>

      </div>

      <div className="formulaire__colonne">
        <h2 className="formulaire__section">Les voyageurs</h2>

      <fieldset className="groupe">
        <legend>Participants</legend>
        <p className="aide aide--haut">
          Les âges et les restrictions guident les suggestions : rythme, choix des
          visites, restaurants adaptés.
        </p>

        {champs.participants.length === 0 && (
          <p className="aide">Aucun participant renseigné pour l’instant.</p>
        )}

        <ul className="participants">
          {champs.participants.map((participant, index) => (
            <li key={participant.cle} className="participant">
              <div className="participant__lignes">
                <div className="champ">
                  <label htmlFor={`participant-nom-${participant.cle}`}>Prénom</label>
                  <input
                    id={`participant-nom-${participant.cle}`}
                    type="text"
                    placeholder="facultatif"
                    value={participant.name}
                    onChange={(e) => modifierParticipant(participant.cle, 'name', e.target.value)}
                  />
                </div>
                <div className="champ">
                  <label htmlFor={`participant-age-${participant.cle}`}>Âge</label>
                  <input
                    id={`participant-age-${participant.cle}`}
                    type="number"
                    min="0"
                    max="120"
                    inputMode="numeric"
                    // Le champ est étroit : « facultatif » y était tronqué.
                    placeholder="ex. 38"
                    value={participant.age}
                    onChange={(e) => modifierParticipant(participant.cle, 'age', e.target.value)}
                  />
                </div>
              </div>

              <div className="champ">
                <label htmlFor={`participant-restrictions-${participant.cle}`}>
                  Restrictions
                </label>
                <input
                  id={`participant-restrictions-${participant.cle}`}
                  type="text"
                  placeholder="sans gluten, mobilité réduite…"
                  value={participant.restrictions}
                  onChange={(e) =>
                    modifierParticipant(participant.cle, 'restrictions', e.target.value)
                  }
                />
                <p className="aide">Séparées par des virgules.</p>
              </div>

              <button
                className="bouton bouton--discret bouton--auto"
                type="button"
                onClick={() => retirerParticipant(participant.cle)}
              >
                Retirer le participant {index + 1}
              </button>
            </li>
          ))}
        </ul>

        <button
          className="bouton bouton--discret bouton--auto"
          type="button"
          onClick={ajouterParticipant}
        >
          Ajouter un participant
        </button>
      </fieldset>

      <div className="champ">
        <label htmlFor="groupDescription">Le groupe et ses contraintes</label>
        <textarea
          id="groupDescription"
          rows="4"
          placeholder="Deux adultes et un enfant de 7 ans, sans voiture, rythme tranquille…"
          value={champs.groupDescription}
          onChange={modifier('groupDescription')}
        />
      </div>

      <div className="champ">
        <label htmlFor="name">Nom du voyage</label>
        <input
          id="name"
          type="text"
          placeholder="Repris de la destination si vide"
          value={champs.name}
          onChange={modifier('name')}
        />
      </div>

      </div>

      <div className="formulaire__pied">
        {erreur && (
          <p className="erreur" role="alert">
            {erreur}
          </p>
        )}

        <div className="actions">
          <button className="bouton" type="submit" disabled={envoi}>
            {envoi ? 'Enregistrement…' : libelleAction}
          </button>
          {onCancel && (
            <button className="bouton bouton--discret" type="button" onClick={onCancel}>
              Annuler
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
